import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Check, ClipboardList, X } from 'lucide-react';
import { Role } from '@nexa/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleGate } from '@/auth/role-gate';
import { cn } from '@/lib/cn';
import type { ReviewItem, ReviewKind } from './types';
import { useResolveReview, useReviewQueue } from './use-review-queue';

// /manual-review route per docs/ui/operator-ui-tds.md §4.1 + §8 (c9, d4, j4).
//
// Supervisor queue of cases/sub-cases flagged for manual handling. Three
// flavors: cost-relaxation requests (c9), permanently-failed bookings (d4),
// supervisor override approvals (j4). All actions gated by
// <RoleGate min={Role.OPS_SUPERVISOR}> at the route level.
//
// Real BFF integration arrives with NEX-72 (route table). The MSW handler
// seeds three rows so the queue is interactive in browser dev.

/** Queue-kind → i18n key (copy lives in `cases.review.kind.*`). */
const KIND_LABEL_KEY: Record<ReviewKind, string> = {
  // The wire kind keeps its name; what is relaxed is a PRICE cap — caps mean the
  // airline's price (commercial-contract P7).
  'cost-relaxation-request': 'review.kind.priceRelaxation',
  'booking-permanent-failure': 'review.kind.bookingFailure',
  'manual-override-approval': 'review.kind.manualOverride',
};

export function ManualReviewRoute() {
  const { t } = useTranslation('cases');
  const queue = useReviewQueue();

  return (
    <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
      <main className="density-page mx-auto flex max-w-4xl flex-col gap-4 p-6">
        <header>
          <h1 className="text-2xl font-semibold">{t('review.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('review.subtitle')}</p>
        </header>

        {queue.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : queue.isError ? (
          <Card>
            <CardContent className="p-6 text-sm text-destructive">
              {t('review.loadError')}
            </CardContent>
          </Card>
        ) : (queue.data ?? []).length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="h-6 w-6" />}
            title={t('review.empty.title')}
            description={t('review.empty.description')}
          />
        ) : (
          <ul className="space-y-2" data-testid="review-queue">
            {(queue.data ?? []).map((item) => (
              <li key={item.reviewUrn}>
                <ReviewRow item={item} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </RoleGate>
  );
}

function ReviewRow({ item }: { item: ReviewItem }) {
  const { t } = useTranslation('cases');
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState<'accept' | 'reject' | null>(null);
  const resolve = useResolveReview();

  const submit = async (accept: boolean): Promise<void> => {
    setActing(accept ? 'accept' : 'reject');
    try {
      await resolve.mutateAsync({
        reviewUrn: item.reviewUrn,
        resolution: { accept, notes },
      });
    } finally {
      setActing(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs uppercase',
                  item.kind === 'booking-permanent-failure' &&
                    'bg-destructive/10 text-destructive',
                  item.kind === 'cost-relaxation-request' &&
                    'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
                  item.kind === 'manual-override-approval' &&
                    'bg-muted text-muted-foreground',
                )}
              >
                {t(KIND_LABEL_KEY[item.kind])}
              </span>
              <Link
                to={`/cases/${item.caseUrn}`}
                className="font-mono text-xs text-primary underline-offset-2 hover:underline"
              >
                {item.airportCode} · {item.caseUrn}
              </Link>
            </div>
            <p className="mt-1 text-sm">{item.summary}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('review.raisedBy', { name: item.raisedBy })} ·{' '}
              {new Date(item.raisedAt).toLocaleTimeString()}
            </p>
            {item.details ? (
              <details className="mt-2 text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">{t('review.details')}</summary>
                <pre className="mt-1 whitespace-pre-wrap font-mono">
                  {JSON.stringify(item.details, null, 2)}
                </pre>
              </details>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('review.notesPlaceholder')}
            className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            rows={2}
            aria-label={t('review.notesLabel')}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void submit(false)}
              disabled={resolve.isPending}
            >
              <X className="mr-1 h-3 w-3" aria-hidden />
              {acting === 'reject' ? t('review.rejecting') : t('review.reject')}
            </Button>
            <Button size="sm" onClick={() => void submit(true)} disabled={resolve.isPending}>
              <Check className="mr-1 h-3 w-3" aria-hidden />
              {acting === 'accept' ? t('review.approving') : t('review.approve')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
